import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { mapWinnersFromApi } from '../utils/winnersApi';
import { getWinnerAmountText, getWinnerMetaItems, getWinnerPrizeLabel, getWinnerTitle } from '../utils/winnerDisplay';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function WinnersBanner({ editMode = false, gameType, maxWinners = 3, onResults }) {
  const { t } = useTranslations();
  const { language } = useLanguage();
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadWinners = async () => {
      setLoading(true);
      try {
        const { tlfId } = getConfig();
        const response = await api.get(`${apiRoutes.SiteWinners}?organizationId=${encodeURIComponent(tlfId)}`);
        const payload = response?.data?.payload || response?.data;
        const data = payload?.data || payload?.Data || payload || [];
        const mapped = mapWinnersFromApi(data, { gameType }).filter(
          (winner) => winner.amount || winner.prize || winner.ticketNumber
        );
        const sliced = mapped.slice(0, maxWinners);
        if (!isMounted) return;
        setWinners(sliced);
        if (typeof onResults === 'function') onResults(sliced.length);
      } catch (error) {
        console.error('Error loading winners banner:', error);
        if (!isMounted) return;
        setWinners([]);
        if (typeof onResults === 'function') onResults(0);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadWinners();
    return () => { isMounted = false; };
  }, [gameType, maxWinners, onResults]);

  if (loading) return null;

  return (
    <div className="bg-amber-500/5 dark:bg-amber-500/10 rounded-2xl p-4 sm:p-6 border-0 sm:border border-amber-500/20 dark:border-amber-500/20 shadow-lg backdrop-blur-sm">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0">
          <div className="bg-amber-500/10 dark:bg-amber-500/20 rounded-xl p-2.5 sm:p-3 border border-amber-500/20">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 mb-3">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{t('recentWinners.title', 'Recent Winners')}</h3>
            <Link
              to="/winners"
              className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 transition-colors"
            >
              {t('recentWinners.viewAll', 'View All Winners')}
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Link>
          </div>

          {winners.length ? (
            <div className="space-y-2.5 sm:space-y-3">
              {winners.map((winner) => {
                const metaItems = getWinnerMetaItems(winner, t).slice(0, 4);
                return (
                  <div key={winner.id} className="rounded-xl bg-white/80 dark:bg-white/5 p-3 sm:p-3.5 border border-amber-500/10 dark:border-white/10 shadow-sm transition-all hover:bg-white dark:hover:bg-white/10">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                          {getWinnerTitle(winner, t)}
                        </p>
                        <p className="font-bold text-sm sm:text-base text-gray-900 dark:text-white truncate">{winner.name}</p>
                      </div>
                      <div className="shrink-0 max-w-[10rem] text-right">
                        <p className="text-[9px] sm:text-[10px] font-semibold uppercase text-green-700 dark:text-green-300">
                          {getWinnerPrizeLabel(winner, t)}
                        </p>
                        <p className="font-black text-sm sm:text-base text-green-600 dark:text-green-400 truncate">
                          {getWinnerAmountText(winner, { language })}
                        </p>
                      </div>
                    </div>
                    {metaItems.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] sm:text-[11px] text-gray-600 dark:text-gray-300">
                        {metaItems.map((item) => (
                          <span key={`${winner.id}-${item.label}`} className="rounded-full bg-amber-500/10 dark:bg-white/5 border border-amber-500/10 dark:border-white/10 px-2 py-0.5">
                            {item.label}: {item.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-amber-300/60 dark:border-amber-700/50 bg-white/60 dark:bg-white/5 p-4 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
              {t('compactWinners.noWinnersYet', 'Winners will appear here after the next draw.')}
            </div>
          )}

          <div className="mt-3.5 pt-3 sm:mt-4 sm:pt-4 border-t border-amber-500/15 dark:border-amber-500/20">
            <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
              <Trophy className="w-3 h-3 text-amber-500 shrink-0" />
              <span>{t('recentWinners.celebratingSupporters', 'Celebrating lucky supporters from recent draws')}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
